import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  BarChart3,
  Users,
  Building2,
  CreditCard,
  Plus,
  Eye,
  TrendingUp,
  AlertCircle,
  Settings,
  Gift,
  Loader
} from 'lucide-react';
import { DashboardCharts } from '../components/DashboardCharts';
import { AdminLayout } from '../components/AdminLayout';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';

const Container = styled.div``;

const PageTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'outline' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: #28a745;
          color: white;
          &:hover {
            background: #218838;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #00d4aa;
          border: 2px solid #00d4aa;
          &:hover {
            background: #00d4aa;
            color: white;
          }
        `;
      default:
        return `
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          &:hover {
            background: #f8f9fa;
          }
        `;
    }
  }}

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatChange = styled.span<{ $positive?: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$positive ? '#28a745' : '#dc3545'};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 640px;
  }
`;

const TableWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e9ecef;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  color: #666;
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'danger' | 'info' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `background: #d4edda; color: #155724;`;
      case 'warning':
        return `background: #fff3cd; color: #856404;`;
      case 'danger':
        return `background: #f8d7da; color: #721c24;`;
      case 'info':
        return `background: #d1ecf1; color: #0c5460;`;
      default:
        return `background: #e9ecef; color: #495057;`;
    }
  }}
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  color: #333;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const ActivityTime = styled.div`
  color: #999;
  font-size: 0.75rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

export const AdminDashboardPage: React.FC = () => {
  const [statistics, setStatistics] = useState<any>(null);
  const [recentCompanies, setRecentCompanies] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showError } = useToast();
  const navigate = useNavigate();

  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [statsResponse, companiesResponse, activityResponse] = await Promise.all([
        api.getAdminDashboardStatistics(),
        api.getAdminDashboardRecentCompanies(),
        api.getAdminDashboardRecentActivity()
      ]);

      if (statsResponse.success) {
        setStatistics(statsResponse.data);
      }

      if (companiesResponse.success) {
        setRecentCompanies(companiesResponse.data);
      }

      if (activityResponse.success) {
        setRecentActivity(activityResponse.data);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showError('Erro ao carregar dados do dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'company_created':
      case 'plus':
        return <Plus size={16} />;
      case 'transaction':
      case 'trending-up':
        return <TrendingUp size={16} />;
      case 'gift':
        return <Gift size={16} />;
      case 'credit_card':
        return <CreditCard size={16} />;
      case 'alert':
        return <AlertCircle size={16} />;
      case 'settings':
        return <Settings size={16} />;
      case 'users':
        return <Users size={16} />;
      default:
        return <BarChart3 size={16} />;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <LoadingContainer>
          <Loader size={48} className="animate-spin" style={{ color: '#00d4aa' }} />
        </LoadingContainer>
      </AdminLayout>
    );
  }

  const stats = statistics ? [
    {
      icon: <Building2 size={20} />,
      value: statistics.companies?.active?.toLocaleString('pt-BR') || '0',
      label: 'Empresas Ativas',
      change: `${statistics.companies?.growth > 0 ? '+' : ''}${statistics.companies?.growth || 0}%`,
      positive: statistics.companies?.growth >= 0,
      color: '#00d4aa'
    },
    {
      icon: <Users size={20} />,
      value: statistics.users?.total?.toLocaleString('pt-BR') || '0',
      label: 'Usuários Totais',
      change: `${statistics.users?.growth > 0 ? '+' : ''}${statistics.users?.growth || 0}%`,
      positive: statistics.users?.growth >= 0,
      color: '#28a745'
    },
    {
      icon: <CreditCard size={20} />,
      value: statistics.transactions?.monthly?.toLocaleString('pt-BR') || '0',
      label: 'Transações Mensais',
      change: `${statistics.transactions?.points_distributed?.toLocaleString('pt-BR') || 0} pts`,
      positive: true,
      color: '#ffc107'
    },
    {
      icon: <TrendingUp size={20} />,
      value: statistics.stores?.total?.toLocaleString('pt-BR') || '0',
      label: 'Lojas Cadastradas',
      change: `${statistics.customers?.total?.toLocaleString('pt-BR') || 0} clientes`,
      positive: true,
      color: '#17a2b8'
    }
  ] : [];

  return (
    <AdminLayout>
      <Container>
        <PageTitle>
          <Title>Dashboard Administrativo</Title>
          <ActionButtons>
            <Button onClick={() => navigate('/admin/companies')}>
              <Eye size={16} />
              Ver Empresas
            </Button>
          </ActionButtons>
        </PageTitle>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index}>
              <StatHeader>
                <StatIcon $color={stat.color}>
                  {stat.icon}
                </StatIcon>
                <StatChange $positive={stat.positive}>
                  {stat.change}
                </StatChange>
              </StatHeader>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>

        <DashboardCharts />

        <ContentGrid>
          <Card>
            <CardHeader>
              <CardTitle>Empresas Recentes</CardTitle>
              <Button onClick={() => navigate('/admin/companies')}>
                <Eye size={14} />
                Ver Todas
              </Button>
            </CardHeader>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Empresa</Th>
                    <Th>Email</Th>
                    <Th>Plano</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {recentCompanies.length > 0 ? (
                    recentCompanies.slice(0, 5).map((company, index) => (
                      <tr key={index}>
                        <Td>{company.name}</Td>
                        <Td>{company.email}</Td>
                        <Td>{company.plan}</Td>
                        <Td>
                          <Badge
                            $variant={
                              company.status === 'ativo' ? 'success' :
                              company.status === 'pendente' ? 'warning' : 'danger'
                            }
                          >
                            {company.status}
                          </Badge>
                        </Td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                        Nenhuma empresa cadastrada ainda
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Atividade Recente</CardTitle>
            </CardHeader>
            <CardContent>
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <ActivityItem key={index}>
                    <ActivityIcon>
                      {getActivityIcon(activity.icon)}
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityText>{activity.text}</ActivityText>
                      <ActivityTime>{activity.time}</ActivityTime>
                    </ActivityContent>
                  </ActivityItem>
                ))
              ) : (
                <ActivityText style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                  Nenhuma atividade recente
                </ActivityText>
              )}
            </CardContent>
          </Card>
        </ContentGrid>
      </Container>
    </AdminLayout>
  );
};
